import {
  AlertTriangle,
  Archive,
  BarChart3,
  Bookmark,
  BookOpenText,
  Bot,
  Brain,
  BriefcaseBusiness,
  CalendarDays,
  FileUp,
  Heart,
  History,
  Inbox,
  Layers,
  LayoutDashboard,
  LibraryBig,
  ListTodo,
  type LucideIcon,
  Map as MapIcon,
  Network,
  NotebookText,
  PanelsTopLeft,
  Puzzle,
  Repeat2,
  RotateCcw,
  Route,
  Search,
  Settings,
  Sparkles,
  Telescope,
  Trash2,
  Workflow,
} from "lucide-react";
import { contextParamFromSearch, parseContextTrail } from "@/shared/lib/contextNavigation";
import { t } from "@/shared/lib/i18n";

export type WorkspaceId =
  | "pages"
  | "planner"
  | "projects"
  | "knowledge"
  | "life"
  | "analysis"
  | "extensions";

export type PageLocation = {
  workspace: string;
  page: string;
};

export type NavigationDestination = {
  id: string;
  workspaceId?: WorkspaceId;
  to: string;
  label: string;
  commandLabel?: string;
  hint?: string;
  icon: LucideIcon;
  matches: (pathname: string) => boolean;
};

export type NavigationWorkspace = {
  id: WorkspaceId;
  to: string;
  label: string;
  mobileLabel?: string;
  description: string;
  icon: LucideIcon;
  destinations: readonly NavigationDestination[];
  matches: (pathname: string) => boolean;
};

function matchesRoot(pathname: string, root: string): boolean {
  return pathname === root || pathname.startsWith(`${root}/`);
}

function matchesAnyRoot(...roots: readonly string[]): (pathname: string) => boolean {
  return (pathname) => roots.some((root) => matchesRoot(pathname, root));
}

function matchesExactly(...paths: readonly string[]): (pathname: string) => boolean {
  return (pathname) => paths.includes(pathname);
}

export const navigationDestinations = {
  pages: {
    id: "pages",
    workspaceId: "pages",
    to: "/sayfalar",
    get label() {
      return t("pages.title");
    },
    get hint() {
      return t("pages.description");
    },
    icon: BookOpenText,
    matches: matchesAnyRoot("/sayfalar"),
  },
  search: {
    id: "search",
    to: "/arama",
    get label() {
      return t("search.page.title");
    },
    get hint() {
      return t("search.page.description");
    },
    icon: Search,
    matches: matchesExactly("/arama"),
  },
  savedViews: {
    id: "saved-views",
    to: "/kayitli-gorunumler",
    get label() {
      return t("savedViews.browse.title");
    },
    get hint() {
      return t("savedViews.browse.lede");
    },
    icon: Bookmark,
    matches: matchesExactly("/kayitli-gorunumler"),
  },
  planner: {
    id: "planner",
    workspaceId: "planner",
    to: "/planlayici",
    get label() {
      return t("copy.5785a01da6e0");
    },
    get hint() {
      return t("copy.baae303c2a0f");
    },
    icon: CalendarDays,
    matches: matchesExactly("/planlayici", "/planlayici/takvim"),
  },
  tasks: {
    id: "tasks",
    workspaceId: "planner",
    to: "/planlayici/gorevler",
    get label() {
      return t("copy.7d03cf59fb3b");
    },
    get hint() {
      return t("copy.58e16151b552");
    },
    icon: ListTodo,
    matches: matchesAnyRoot("/planlayici/gorevler", "/gorev"),
  },
  routines: {
    id: "routines",
    workspaceId: "planner",
    to: "/planlayici/rutinler",
    get label() {
      return t("copy.dd14cd6ede95");
    },
    icon: Repeat2,
    matches: matchesAnyRoot("/planlayici/rutinler", "/rutin"),
  },
  plans: {
    id: "plans",
    workspaceId: "planner",
    to: "/planlayici/planlar",
    get label() {
      return t("copy.aa6a83bfcde1");
    },
    get hint() {
      return t("copy.2f4bf8602006");
    },
    icon: Route,
    matches: matchesAnyRoot("/planlayici/planlar", "/plan"),
  },
  workdesk: {
    id: "workdesk",
    workspaceId: "projects",
    to: "/calisma",
    get label() {
      return t("copy.9cba11f2a318");
    },
    get hint() {
      return t("copy.4068257604e3");
    },
    icon: PanelsTopLeft,
    matches: matchesExactly("/calisma"),
  },
  focus: {
    id: "focus",
    workspaceId: "projects",
    to: "/calisma/odak",
    get label() {
      return t("copy.41620582d621");
    },
    icon: Sparkles,
    matches: matchesAnyRoot("/calisma/odak"),
  },
  projects: {
    id: "projects",
    workspaceId: "projects",
    to: "/calisma/projeler",
    get label() {
      return t("copy.30bcb5bccd3e");
    },
    get hint() {
      return t("copy.cf33e2c6e655");
    },
    icon: BriefcaseBusiness,
    matches: matchesAnyRoot("/calisma/projeler"),
  },
  notes: {
    id: "notes",
    workspaceId: "knowledge",
    to: "/hafiza/notlar",
    get label() {
      return t("copy.959f1d1df29f");
    },
    icon: NotebookText,
    matches: (pathname) => pathname === "/hafiza/notlar" || matchesRoot(pathname, "/not"),
  },
  memoryKnowledgeSpace: {
    id: "memory-knowledge-space",
    workspaceId: "knowledge",
    to: "/hafiza/bilgi-alani",
    get label() {
      return t("copy.e047cbc6ad0b");
    },
    get hint() {
      return t("copy.0603d7e36963");
    },
    icon: LibraryBig,
    matches: matchesAnyRoot("/hafiza/bilgi-alani"),
  },
  drafts: {
    id: "drafts",
    workspaceId: "knowledge",
    to: "/hafiza/taslaklar",
    get label() {
      return t("copy.ee541d33413a");
    },
    icon: Inbox,
    matches: matchesAnyRoot("/hafiza/taslaklar", "/hafiza/triage"),
  },
  imports: {
    id: "declarative-import",
    workspaceId: "knowledge",
    to: "/hafiza/ice-aktar",
    get label() {
      return t("imports.navigation.label");
    },
    get hint() {
      return t("imports.navigation.hint");
    },
    icon: FileUp,
    matches: matchesExactly("/hafiza/ice-aktar"),
  },
  archive: {
    id: "archive",
    to: "/arsiv",
    get label() {
      return t("copy.b634acc71119");
    },
    icon: Archive,
    matches: matchesAnyRoot("/arsiv"),
  },
  trash: {
    id: "trash",
    to: "/cop-kutusu",
    get label() {
      return t("copy.009e9385f1fb");
    },
    icon: Trash2,
    matches: matchesAnyRoot("/cop-kutusu"),
  },
  lifeTracking: {
    id: "life-tracking",
    workspaceId: "life",
    to: "/yasam/takip",
    get label() {
      return t("copy.b377e5f91efa");
    },
    get commandLabel() {
      return t("copy.b377e5f91efa");
    },
    get hint() {
      return t("copy.dda0b6d08a1c");
    },
    icon: CalendarDays,
    matches: matchesAnyRoot("/yasam/takip"),
  },
  knowledgeItems: {
    id: "knowledge-items",
    workspaceId: "knowledge",
    to: "/hafiza/bilgiler",
    get label() {
      return t("copy.549645665df6");
    },
    get hint() {
      return t("copy.7796f329d977");
    },
    icon: Network,
    matches: matchesAnyRoot("/hafiza/bilgiler"),
  },
  conceptMaps: {
    id: "concept-maps",
    workspaceId: "knowledge",
    to: "/hafiza/kavram-haritasi",
    get label() {
      return t("copy.98396a60bb07");
    },
    icon: MapIcon,
    matches: matchesAnyRoot("/hafiza/kavram-haritasi"),
  },
  lifeAreas: {
    id: "life-areas",
    workspaceId: "life",
    to: "/yasam/alanlar",
    get label() {
      return t("copy.11e580db2092");
    },
    get commandLabel() {
      return t("copy.11e580db2092");
    },
    get hint() {
      return t("copy.d1fed43771b2");
    },
    icon: LayoutDashboard,
    matches: matchesAnyRoot("/yasam/alanlar", "/yasam/alan", "/yasam/tablo", "/yasam/kayit"),
  },
  lifeInbox: {
    id: "life-inbox",
    workspaceId: "life",
    to: "/yasam/bildirimler",
    get label() {
      return t("copy.fc2cbca9acbe");
    },
    icon: Inbox,
    matches: matchesAnyRoot("/yasam/bildirimler"),
  },
  lifeMetrics: {
    id: "life-metrics",
    workspaceId: "life",
    to: "/yasam/gostergeler",
    get label() {
      return t("life.workspace.metrics");
    },
    icon: BarChart3,
    matches: matchesAnyRoot("/yasam/gostergeler"),
  },
  lifeRules: {
    id: "life-rules",
    workspaceId: "life",
    to: "/yasam/kurallar",
    get label() {
      return t("life.workspace.rules");
    },
    icon: Workflow,
    matches: matchesAnyRoot("/yasam/kurallar"),
  },
  lifeGuide: {
    id: "life-guide",
    workspaceId: "life",
    to: "/yasam/rehber",
    get label() {
      return t("copy.67ed883ba47d");
    },
    get commandLabel() {
      return t("copy.67ed883ba47d");
    },
    get hint() {
      return t("copy.4a6c03490a93");
    },
    icon: BookOpenText,
    matches: matchesAnyRoot("/yasam/rehber"),
  },
  spans: {
    id: "spans",
    workspaceId: "planner",
    to: "/planlayici/donemler",
    get label() {
      return t("copy.6074721d9400");
    },
    icon: Layers,
    matches: matchesAnyRoot("/planlayici/donemler"),
  },
  weeklyReset: {
    id: "weekly-reset",
    workspaceId: "planner",
    to: "/planlayici/haftalik",
    get label() {
      return t("copy.1a27744cbf0b");
    },
    icon: RotateCcw,
    matches: matchesAnyRoot("/planlayici/haftalik"),
  },
  adaptation: {
    id: "adaptation",
    workspaceId: "planner",
    to: "/planlayici/uyarlama",
    get label() {
      return t("adaptation.title");
    },
    get hint() {
      return t("adaptation.lede");
    },
    icon: AlertTriangle,
    matches: matchesAnyRoot("/planlayici/uyarlama"),
  },
  analysis: {
    id: "analysis",
    workspaceId: "analysis",
    to: "/analiz",
    get label() {
      return t("copy.961304f44e7c");
    },
    get hint() {
      return t("copy.2b0919f10d2e");
    },
    icon: BarChart3,
    matches: matchesExactly("/analiz"),
  },
  rediscover: {
    id: "rediscover",
    workspaceId: "analysis",
    to: "/analiz/yeniden-kesfet",
    get label() {
      return t("copy.2d8c97defc9e");
    },
    icon: Telescope,
    matches: matchesAnyRoot("/analiz/yeniden-kesfet"),
  },
  memory: {
    id: "memory",
    workspaceId: "analysis",
    to: "/analiz/gecmis",
    get label() {
      return t("copy.d320a13654f8");
    },
    icon: History,
    matches: matchesAnyRoot("/analiz/gecmis"),
  },
  extensions: {
    id: "extensions",
    workspaceId: "extensions",
    to: "/eklentiler",
    get label() {
      return t("copy.2c8d8cd444eb");
    },
    icon: Puzzle,
    matches: matchesExactly("/eklentiler"),
  },
  settings: {
    id: "settings",
    to: "/ayarlar",
    get label() {
      return t("copy.80ad54ad05fa");
    },
    icon: Settings,
    matches: matchesAnyRoot("/ayarlar"),
  },
  assistant: {
    id: "assistant",
    to: "/asistan",
    get label() {
      return t("assistant.page.title");
    },
    get hint() {
      return t("assistant.page.lede");
    },
    icon: Bot,
    matches: matchesExactly("/asistan"),
  },
} as const satisfies Record<string, NavigationDestination>;

const pagesWorkspace: NavigationWorkspace = {
  id: "pages",
  to: "/sayfalar",
  get label() {
    return t("pages.title");
  },
  get description() {
    return t("pages.description");
  },
  icon: BookOpenText,
  matches: matchesAnyRoot("/sayfalar"),
  destinations: [navigationDestinations.pages],
};

const plannerWorkspace: NavigationWorkspace = {
  id: "planner",
  to: navigationDestinations.planner.to,
  get label() {
    return t("copy.e1a10bdd81df");
  },
  get mobileLabel() {
    return t("copy.e1a10bdd81df");
  },
  get description() {
    return t("copy.f86dcfafd6d1");
  },
  icon: CalendarDays,
  matches: matchesAnyRoot("/planlayici", "/plan", "/gorev", "/rutin"),
  destinations: [
    navigationDestinations.planner,
    navigationDestinations.tasks,
    navigationDestinations.routines,
    navigationDestinations.plans,
    navigationDestinations.spans,
    navigationDestinations.weeklyReset,
    navigationDestinations.adaptation,
  ],
};

const projectsWorkspace: NavigationWorkspace = {
  id: "projects",
  to: navigationDestinations.projects.to,
  get label() {
    return t("copy.b061c83841e3");
  },
  get description() {
    return t("copy.29a87f2ba4f6");
  },
  icon: BriefcaseBusiness,
  matches: matchesAnyRoot("/calisma", "/proje"),
  destinations: [
    navigationDestinations.projects,
    navigationDestinations.workdesk,
    navigationDestinations.focus,
  ],
};

const knowledgeWorkspace: NavigationWorkspace = {
  id: "knowledge",
  to: navigationDestinations.notes.to,
  get label() {
    return t("copy.26dade3a1507");
  },
  get description() {
    return t("copy.0684f4eb38a7");
  },
  icon: Brain,
  matches: matchesAnyRoot("/hafiza", "/not"),
  destinations: [
    navigationDestinations.notes,
    navigationDestinations.memoryKnowledgeSpace,
    navigationDestinations.drafts,
    navigationDestinations.imports,
    navigationDestinations.knowledgeItems,
    navigationDestinations.conceptMaps,
  ],
};

const lifeWorkspace: NavigationWorkspace = {
  id: "life",
  to: navigationDestinations.lifeTracking.to,
  get label() {
    return t("copy.71317e48f579");
  },
  get description() {
    return t("copy.6ba50b24e4ae");
  },
  icon: Heart,
  matches: matchesAnyRoot("/yasam"),
  destinations: [
    navigationDestinations.lifeTracking,
    navigationDestinations.lifeGuide,
    navigationDestinations.lifeAreas,
    navigationDestinations.lifeMetrics,
    navigationDestinations.lifeRules,
    navigationDestinations.lifeInbox,
  ],
};

const analysisWorkspace: NavigationWorkspace = {
  id: "analysis",
  to: navigationDestinations.analysis.to,
  get label() {
    return t("copy.961304f44e7c");
  },
  get description() {
    return t("copy.c46cfde0f057");
  },
  icon: BarChart3,
  matches: matchesAnyRoot("/analiz"),
  destinations: [
    navigationDestinations.analysis,
    navigationDestinations.rediscover,
    navigationDestinations.memory,
  ],
};

const extensionsWorkspace: NavigationWorkspace = {
  id: "extensions",
  to: navigationDestinations.extensions.to,
  get label() {
    return t("copy.2c8d8cd444eb");
  },
  get description() {
    return t("extensions.workspace.description");
  },
  icon: Puzzle,
  matches: matchesAnyRoot("/eklentiler"),
  destinations: [navigationDestinations.extensions],
};

/** Optional apps keep their own route while belonging to one visible workspace. */
export function withOptionalDestinations(
  workspace: NavigationWorkspace,
  optional: readonly NavigationDestination[],
): NavigationWorkspace {
  return workspace.id === "extensions"
    ? { ...workspace, destinations: [...workspace.destinations, ...optional] }
    : workspace;
}

export const navigationWorkspaces: readonly NavigationWorkspace[] = [
  pagesWorkspace,
  plannerWorkspace,
  projectsWorkspace,
  knowledgeWorkspace,
  lifeWorkspace,
  analysisWorkspace,
  extensionsWorkspace,
];

export const mobilePrimaryWorkspaces: readonly NavigationWorkspace[] = [
  lifeWorkspace,
  projectsWorkspace,
  plannerWorkspace,
  knowledgeWorkspace,
  analysisWorkspace,
];

export const workspaceStartPaths = {
  pages: "/sayfalar",
  planner: "/planlayici",
  projects: "/calisma/projeler",
  knowledge: "/hafiza/notlar",
  life: "/yasam/takip",
  analysis: "/analiz",
  extensions: "/eklentiler",
} as const satisfies Record<WorkspaceId, string>;

/**
 * Where the application opens.
 *
 * Pages is the default workspace: an account that has never chosen — or whose
 * stored value is not one this client knows — lands there. An account that did
 * choose keeps its choice, which is why the lookup comes first.
 *
 * `pagesEnabled` exists because Pages is behind a server capability flag. When
 * the server says the workspace is off, sending anybody to it would open a
 * screen that only explains it is unavailable, so the previous default stands
 * in instead.
 */
export function getWorkspaceStartPath(
  workspace: string | null | undefined,
  options?: { pagesEnabled?: boolean },
) {
  const pagesEnabled = options?.pagesEnabled ?? true;
  const chosen =
    workspace && Object.hasOwn(workspaceStartPaths, workspace)
      ? workspaceStartPaths[workspace as WorkspaceId]
      : workspaceStartPaths.pages;
  return chosen === workspaceStartPaths.pages && !pagesEnabled
    ? workspaceStartPaths.planner
    : chosen;
}

export type MobileMoreGroup = {
  workspace: NavigationWorkspace;
  destinations: readonly NavigationDestination[];
};

export const mobileMoreGroups: readonly MobileMoreGroup[] = [
  { workspace: pagesWorkspace, destinations: pagesWorkspace.destinations },
  {
    workspace: plannerWorkspace,
    destinations: plannerWorkspace.destinations,
  },
  {
    workspace: projectsWorkspace,
    destinations: projectsWorkspace.destinations,
  },
  {
    workspace: knowledgeWorkspace,
    destinations: knowledgeWorkspace.destinations,
  },
  {
    workspace: lifeWorkspace,
    destinations: lifeWorkspace.destinations,
  },
  {
    workspace: analysisWorkspace,
    destinations: analysisWorkspace.destinations,
  },
  {
    workspace: extensionsWorkspace,
    destinations: extensionsWorkspace.destinations,
  },
];

export const commandDestinations: readonly NavigationDestination[] = [
  navigationDestinations.pages,
  navigationDestinations.planner,
  navigationDestinations.tasks,
  navigationDestinations.search,
  navigationDestinations.savedViews,
  navigationDestinations.routines,
  navigationDestinations.plans,
  navigationDestinations.spans,
  navigationDestinations.weeklyReset,
  navigationDestinations.adaptation,
  navigationDestinations.projects,
  navigationDestinations.workdesk,
  navigationDestinations.focus,
  navigationDestinations.notes,
  navigationDestinations.memoryKnowledgeSpace,
  navigationDestinations.drafts,
  navigationDestinations.imports,
  navigationDestinations.knowledgeItems,
  navigationDestinations.conceptMaps,
  navigationDestinations.lifeTracking,
  navigationDestinations.lifeGuide,
  navigationDestinations.lifeAreas,
  navigationDestinations.lifeMetrics,
  navigationDestinations.lifeRules,
  navigationDestinations.lifeInbox,
  navigationDestinations.analysis,
  navigationDestinations.rediscover,
  navigationDestinations.memory,
  navigationDestinations.extensions,
  navigationDestinations.assistant,
  navigationDestinations.settings,
];

type PageLocationRule = {
  matches: (pathname: string) => boolean;
  workspaceId: WorkspaceId;
  page: string;
};

const pageLocationOverrides: readonly PageLocationRule[] = [
  {
    matches: (pathname) => pathname.startsWith("/hafiza/triage/"),
    workspaceId: "knowledge",
    get page() {
      return t("refinement.intake.title");
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/plan/"),
    workspaceId: "planner",
    get page() {
      return t("copy.3fc29bcc049b");
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/calisma/projeler/"),
    workspaceId: "projects",
    get page() {
      return t("copy.a5980e637f70");
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/gorev/"),
    workspaceId: "planner",
    get page() {
      return t("copy.4c4df10d00cb");
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/rutin/"),
    workspaceId: "planner",
    get page() {
      return t("copy.f03037978607");
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/not/"),
    workspaceId: "knowledge",
    get page() {
      return t("copy.5b2983bb53d0");
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/yasam/alan/"),
    workspaceId: "life",
    get page() {
      return t("copy.4bacae8886aa");
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/yasam/tablo/"),
    workspaceId: "life",
    get page() {
      return t("copy.2eeda58d5020");
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/yasam/kayit/"),
    workspaceId: "life",
    get page() {
      return t("copy.c335f493996c");
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/planlayici/donemler/"),
    workspaceId: "planner",
    get page() {
      return t("copy.c9a1e072cd88");
    },
  },
];

const workspaceById = new Map(
  navigationWorkspaces.map((workspace) => [workspace.id, workspace] as const),
);

export function getWorkspaceForPathname(pathname: string): NavigationWorkspace | undefined {
  return navigationWorkspaces.find((workspace) => workspace.matches(pathname));
}

/**
 * Resolves the selected peer inside the active workspace.
 *
 * Detail routes such as `/gorev/:id` can be opened from a different workspace
 * through a validated context trail. In that case pathname matching alone
 * cannot choose the right peer tab, so the root context supplies the owning
 * surface without turning arbitrary query text into navigation state.
 */
export function getWorkspaceActiveDestination(
  workspace: NavigationWorkspace,
  pathname: string,
  search?: unknown,
): NavigationDestination | undefined {
  const root = parseContextTrail(contextParamFromSearch(search))[0];
  if (!root) {
    return workspace.destinations.find((destination) => destination.matches(pathname));
  }

  if (workspace.id === "projects") {
    if (root.kind === "project") {
      if (root.view === "workdesk") return navigationDestinations.workdesk;
      return navigationDestinations.projects;
    }
    if (root.kind === "workspace" && root.id === "projects") {
      return navigationDestinations.projects;
    }
  }

  if (workspace.id === "planner") {
    if (root.kind === "task") return navigationDestinations.tasks;
    if (root.kind === "plan") return navigationDestinations.plans;
    if (root.kind === "routine") return navigationDestinations.routines;
    if (root.kind === "span") return navigationDestinations.spans;
    if (root.kind === "workspace") {
      if (root.id === "tasks") return navigationDestinations.tasks;
      if (root.id === "plans") return navigationDestinations.plans;
      if (root.id === "routines") return navigationDestinations.routines;
      if (root.id === "orientation") return navigationDestinations.planner;
      if (root.id === "planner") return navigationDestinations.planner;
    }
  }

  if (workspace.id === "knowledge") {
    if (root.kind === "note") {
      return root.view === "map"
        ? navigationDestinations.conceptMaps
        : navigationDestinations.notes;
    }
    if (root.kind === "knowledge_item") return navigationDestinations.knowledgeItems;
    if (root.kind === "knowledge_map") return navigationDestinations.conceptMaps;
    if (root.kind === "workspace") {
      if (root.id === "notes") return navigationDestinations.notes;
      if (root.id === "knowledge") return navigationDestinations.knowledgeItems;
    }
  }

  if (workspace.id === "life" && root.kind === "workspace" && root.id === "orientation") {
    return navigationDestinations.lifeGuide;
  }

  if (workspace.id === "analysis" && root.kind === "analysis") {
    return root.view === "history"
      ? navigationDestinations.memory
      : navigationDestinations.analysis;
  }

  return workspace.destinations.find((destination) => destination.matches(pathname));
}

export function isWorkspaceActive(workspaceId: WorkspaceId, pathname: string): boolean {
  return getWorkspaceForPathname(pathname)?.id === workspaceId;
}

export function getPageLocation(pathname: string): PageLocation {
  const override = pageLocationOverrides.find((rule) => rule.matches(pathname));
  if (override) {
    const workspace = workspaceById.get(override.workspaceId);
    return { workspace: workspace?.label ?? "Exognitos", page: override.page };
  }

  const extensionPage = {
    "/eklentiler/muzik": "copy.fd3b91f48bcf",
    "/eklentiler/dersler": "copy.e15ab1b0024c",
    "/eklentiler/bisiklet": "copy.1f85b0be8bfb",
    "/eklentiler/cihazlar": "copy.8d4d1ebe70cb",
    "/eklentiler/sosyal": "copy.ac84369bcfc5",
    "/eklentiler/uygulama-tercihleri": "copy.4a758db5ca03",
  } as Record<string, string>;
  if (extensionPage[pathname]) {
    return { workspace: extensionsWorkspace.label, page: t(extensionPage[pathname]) };
  }

  if (navigationDestinations.assistant.matches(pathname)) {
    return { workspace: "Exognitos", page: navigationDestinations.assistant.label };
  }

  if (navigationDestinations.savedViews.matches(pathname)) {
    return { workspace: "Exognitos", page: navigationDestinations.savedViews.label };
  }

  const settingsPage = {
    "/ayarlar/profil": "profile.title",
    "/ayarlar/bagli-asistanlar": "connections.title",
    "/ayarlar/asistan-hafizasi": "assistantMemory.title",
    "/ayarlar/etkinlik-guvenlik": "activity.title",
    "/ayarlar/gizlilik": "privacy.title",
  } as Record<string, string>;
  if (settingsPage[pathname]) {
    return { workspace: navigationDestinations.settings.label, page: t(settingsPage[pathname]) };
  }

  if (navigationDestinations.settings.matches(pathname)) {
    return { workspace: t("copy.1c466db79243"), page: navigationDestinations.settings.label };
  }

  if (navigationDestinations.archive.matches(pathname)) {
    return { workspace: t("copy.1c466db79243"), page: navigationDestinations.archive.label };
  }

  if (navigationDestinations.trash.matches(pathname)) {
    return { workspace: t("copy.1c466db79243"), page: navigationDestinations.trash.label };
  }

  const destination = Object.values(navigationDestinations).find((item) => item.matches(pathname));
  if (destination && "workspaceId" in destination && destination.workspaceId) {
    const workspace = workspaceById.get(destination.workspaceId);
    if (workspace) return { workspace: workspace.label, page: destination.label };
  }

  const workspace = getWorkspaceForPathname(pathname);
  if (workspace) return { workspace: workspace.label, page: workspace.label };
  return { workspace: "Exognitos", page: t("copy.9299e36c6bec") };
}

export function getDocumentTitle(pathname: string): string {
  const location = getPageLocation(pathname);
  return `${location.page} · Exognitos`;
}
